<template>
  <div dir="auto">
    <div
      class="flex justify-between items-center w-full"
      :class="{ 'mb-2': !field.description }"
    >
      <label class="label text-2xl">
        <MarkdownContent v-if="field.title" :string="field.title" />
        <template v-else>
          {{ showFieldNames && field.name ? field.name : t("initials") }}
        </template>
      </label>
      <div class="space-x-2 flex">
        <span
          v-if="isDrawInitials"
          class="tooltip"
          :data-tip="t('type_initial')"
        >
          <a
            id="type_text_button"
            href="#"
            class="btn btn-outline font-medium btn-sm"
            @click.prevent="toggleTextInput"
          >
            <IconTextSize :width="16" />
            <span class="hidden sm:inline">
              {{ t("type") }}
            </span>
          </a>
        </span>
        <span v-else class="tooltip ml-2" :data-tip="t('draw_initials')">
          <a
            id="type_text_button"
            href="#"
            class="btn btn-outline font-medium btn-sm"
            @click.prevent="toggleTextInput"
          >
            <IconSignature :width="16" />
            <span class="hidden sm:inline">
              {{ t("draw") }}
            </span>
          </a>
        </span>
        <span
          v-if="
            field.preferences?.format !== 'typed' &&
            field.preferences?.format !== 'drawn'
          "
          class="tooltip"
          :data-tip="t('take_photo')"
        >
          <label
            class="btn btn-outline btn-sm font-medium inline-flex flex-nowrap"
          >
            <IconCamera :width="16" />
            <input
              :key="uploadImageInputKey"
              type="file"
              hidden
              accept="image/*"
              @change="drawImage"
            />
            <span class="hidden sm:inline">{{ t("upload") }}</span>
          </label>
        </span>
        <a
          v-if="modelValue || computedPreviousValue"
          href="#"
          class="btn font-medium btn-outline btn-sm"
          @click.prevent="remove"
        >
          <IconReload :width="16" />
          {{ t("clear") }}
        </a>
        <a
          v-else
          href="#"
          class="btn font-medium btn-outline btn-sm"
          @click.prevent="clear"
        >
          <IconReload :width="16" />
          {{ t("clear") }}
        </a>
        <a
          :title="t('minimize')"
          href="#"
          class="py-1.5 inline md:hidden"
          @click.prevent="$emit('minimize')"
        >
          <IconArrowsDiagonalMinimize2 :width="20" :height="20" />
        </a>
      </div>
    </div>
    <div v-if="field.description" dir="auto" class="mb-3 px-1">
      <MarkdownContent :string="field.description" />
    </div>
    <AppearsOn :field="field" />
    <input
      :value="modelValue || computedPreviousValue"
      type="hidden"
      :name="`values[${field.uuid}]`"
    />
    <img
      v-if="modelValue || computedPreviousValue"
      :src="attachmentsIndex[modelValue || computedPreviousValue].url"
      class="mx-auto bg-white border border-base-300 rounded max-h-72"
    />
    <canvas
      v-show="!modelValue && !computedPreviousValue"
      ref="canvas"
      class="bg-white border border-base-300 rounded-2xl w-full"
    />
    <div
      v-show="!modelValue && !computedPreviousValue && isDrawInitials"
      class="mt-2 flex flex-wrap gap-2"
    >
      <span
        class="w-6 h-6 rounded-full cursor-pointer bg-black border border-base-300"
        data-color="#000000"
        @click="drawingColor = '#000000'"
      ></span>
      <span
        class="w-6 h-6 rounded-full cursor-pointer bg-blue-500 border border-base-300"
        data-color="#3b82f6"
        @click="drawingColor = '#3b82f6'"
      ></span>
      <span
        class="w-6 h-6 rounded-full cursor-pointer bg-red-500 border border-base-300"
        data-color="#ef4444"
        @click="drawingColor = '#ef4444'"
      ></span>
      <span
        class="w-6 h-6 rounded-full cursor-pointer bg-white border border-base-300"
        data-color="#ffffff"
        style="box-shadow: 0 0 0 1px #ccc"
        @click="drawingColor = '#ffffff'"
      ></span>
    </div>
    <input
      v-if="!isDrawInitials && !modelValue && !computedPreviousValue"
      id="initials_text_input"
      ref="textInput"
      class="base-input !text-2xl w-full mt-6 text-center"
      :required="field.required && !isInitialsStarted && !modelValue && !computedPreviousValue"
      :placeholder="`${t('type_initial_here')}...`"
      type="text"
      @focus="$emit('focus')"
      @input="updateWrittenInitials"
    />
    <div
      v-if="!isDrawInitials && $refs.textInput && $refs.textInput.value"
      class="mt-2 flex flex-wrap gap-2 items-center"
    >
      <select
        v-model="selectedFont"
        class="select select-bordered select-sm"
        @change="updateWrittenInitials"
      >
        <option v-for="font in availableFonts" :key="font" :value="font">
          {{ font }}
        </option>
      </select>
      <div class="relative inline-block">
        <input
          v-model.number="fontSize"
          type="number"
          min="1"
          max="100"
          step="1"
          class="input input-bordered input-sm w-20 pr-8"
          @input="updateFontSize"
        />
        <select
          v-model="fontSize"
          class="absolute inset-y-0 right-0 w-8 opacity-0 cursor-pointer"
          @change="updateFontSize"
        >
          <option v-for="size in sortedFontSizes" :key="size" :value="size">
            {{ size }} px
          </option>
        </select>
      </div>
      <button
        class="btn btn-sm"
        :class="{ 'btn-active': isBold }"
        @click.prevent="toggleBold"
      >
        B
      </button>
      <button
        class="btn btn-sm"
        :class="{ 'btn-active': isItalic }"
        @click.prevent="toggleItalic"
      >
        I
      </button>
      <button
        class="btn btn-sm"
        :class="{ 'btn-active': isUnderline }"
        @click.prevent="toggleUnderline"
      >
        U
      </button>
      <div class="flex gap-1">
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-black border border-base-300"
          data-color="#000000"
          @click="
            textColor = '#000000';
            updateWrittenInitials();
          "
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-blue-500 border border-base-300"
          data-color="#3b82f6"
          @click="
            textColor = '#3b82f6';
            updateWrittenInitials();
          "
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-red-500 border border-base-300"
          data-color="#ef4444"
          @click="
            textColor = '#ef4444';
            updateWrittenInitials();
          "
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-white border border-base-300"
          data-color="#ffffff"
          style="box-shadow: 0 0 0 1px #ccc"
          @click="
            textColor = '#ffffff';
            updateWrittenInitials();
          "
        ></span>
      </div>
    </div>
  </div>
</template>

<script>
import { cropCanvasAndExportToPNG } from "./crop_canvas";
import {
  IconReload,
  IconCamera,
  IconTextSize,
  IconSignature,
  IconArrowsDiagonalMinimize2,
} from "@tabler/icons-vue";
import SignaturePad from "signature_pad";
import AppearsOn from "./appears_on";
import MarkdownContent from "./markdown_content";

const isFontLoaded = false;
const scale = 3;

export default {
  name: "InitialsStep",
  components: {
    AppearsOn,
    IconReload,
    IconCamera,
    IconTextSize,
    IconSignature,
    MarkdownContent,
    IconArrowsDiagonalMinimize2,
  },
  inject: ["baseUrl", "t"],
  props: {
    field: {
      type: Object,
      required: true,
    },
    dryRun: {
      type: Boolean,
      required: false,
      default: false,
    },
    submitterSlug: {
      type: String,
      required: true,
    },
    showFieldNames: {
      type: Boolean,
      required: false,
      default: true,
    },
    attachmentsIndex: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    previousValue: {
      type: String,
      required: false,
      default: "",
    },
    modelValue: {
      type: String,
      required: false,
      default: "",
    },
    userInitials: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  emits: ["attached", "update:model-value", "start", "minimize", "focus", "profile-updated"],
  data() {
    return {
      isInitialsStarted: !!this.previousValue,
      isUsePreviousValue: true,
      isDrawInitials: false,
      uploadImageInputKey: Math.random().toString(),
      textColor: "#000000",
      drawingColor: "#000000",
      availableFonts: [
        "Arial",
        "Times New Roman",
        "Courier New",
        "Georgia",
        "Lucida Sans",
        "Verdana",
        "Impact",
        "Italianno",
        "Cookie",
        "Parisienne",
      ],
      selectedFont: localStorage.getItem("selectedFontInitials") || "Arial",
      fontSize:
        parseInt(localStorage.getItem("selectedFontSizeInitials")) || 16,
      fontSizes: JSON.parse(localStorage.getItem("fontSizes")) || [
        8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72,
      ],
      isBold: false,
      isItalic: false,
      isUnderline: false,
      // Store previews for both typed and drawn modes
      typedInitialsPreview: this.userInitials?.typed || '',
      drawnInitialsPreview: this.userInitials?.drawn || '',
      modeToggled: false
    };
  },
  computed: {
    computedPreviousValue() {
      if (!this.isUsePreviousValue) {
        return null;
      }

      // 1. Mode-specific profile value takes priority (drawn and typed are independent)
      const modeKey = this.isDrawInitials ? 'drawn' : 'typed';
      const modeSpecificValue = this.userInitials?.[modeKey];
      if (modeSpecificValue && this.attachmentsIndex[modeSpecificValue]) {
        return modeSpecificValue;
      }

      // 2. Only fall back to previousValue if user hasn't explicitly toggled modes
      //    (previousValue is mode-agnostic and would show drawn value in typed mode)
      if (!this.modeToggled && this.previousValue && this.attachmentsIndex[this.previousValue]) {
        return this.previousValue;
      }

      return null;
    },
    sortedFontSizes() {
      return [...new Set(this.fontSizes)].sort((a, b) => a - b);
    },
  },
  watch: {
    modelValue(newValue) {
      if (newValue) {
        this.isInitialsStarted = true;
      }
    },
    computedPreviousValue(newValue) {
      if (newValue && !this.modelValue) {
        this.isInitialsStarted = true;
      }
    },
    fontSizes: {
      handler(newFontSizes) {
        localStorage.setItem("fontSizes", JSON.stringify(newFontSizes));
      },
      deep: true,
    },
    drawingColor(newColor) {
      if (this.pad) {
        this.pad.penColor = newColor;
      }
    },
  },
  async mounted() {
    this.$nextTick(() => {
      if (this.$refs.canvas) {
        this.$refs.canvas.width =
          this.$refs.canvas.parentNode.clientWidth * scale;
        this.$refs.canvas.height =
          (this.$refs.canvas.parentNode.clientWidth / 4.5) * scale;

        this.$refs.canvas.getContext("2d").scale(scale, scale);
      }
    });

    if (this.$refs.canvas) {
      this.pad = new SignaturePad(this.$refs.canvas, {
        penColor: this.drawingColor,
      });

      this.pad.addEventListener("endStroke", () => {
        if (!this.isInitialsStarted) {
          this.isInitialsStarted = true;
          this.$emit("start");
        }
      });

      this.intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.$refs.canvas.width =
                this.$refs.canvas.parentNode.clientWidth * scale;
              this.$refs.canvas.height =
                (this.$refs.canvas.parentNode.clientWidth / 4.5) * scale;

              this.$refs.canvas.getContext("2d").scale(scale, scale);

              this.intersectionObserver?.disconnect();
            }
          });
        }
      );

      this.intersectionObserver.observe(this.$refs.canvas);
    }

    if (this.modelValue || this.computedPreviousValue) {
      this.isInitialsStarted = true;
    }
  },
  beforeUnmount() {
    this.intersectionObserver?.disconnect();
  },
  methods: {
    remove() {
      this.$emit("update:model-value", "");

      this.isUsePreviousValue = false;
      this.isInitialsStarted = false;
    },
    clear() {
      this.pad.clear();

      this.isInitialsStarted = false;

      if (this.$refs.textInput) {
        this.$refs.textInput.value = "";
      }
    },
    updateWrittenInitials() {
      if (!this.$refs.textInput) return;

      // Auto-clear preview when user starts typing — new input replaces previous
      if (this.modelValue || this.isUsePreviousValue) {
        this.$emit('update:model-value', '');
        this.isUsePreviousValue = false;
      }

      const text = this.$refs.textInput.value;

      this.isInitialsStarted = !!text;

      const canvas = this.$refs.canvas;
      const context = canvas.getContext("2d");
      const fontFamily = this.selectedFont;
      const fontSize = this.fontSize;
      const fontStyle = this.isItalic ? "italic" : "normal";
      const fontWeight = this.isBold ? "bold" : "normal";

      context.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.clearRect(0, 0, canvas.width / scale, canvas.height / scale);
      context.fillStyle = this.textColor;

      const x = canvas.width / (2 * scale);
      const y = canvas.height / (2 * scale);

      context.fillText(text, x, y);

      if (this.isUnderline) {
        const textWidth = context.measureText(text).width;
        const underlineY = y + fontSize / 2;
        context.beginPath();
        context.moveTo(x - textWidth / 2, underlineY);
        context.lineTo(x + textWidth / 2, underlineY);
        context.strokeStyle = this.textColor;
        context.stroke();
      }

      localStorage.setItem("selectedFontSizeInitials", this.fontSize);
      localStorage.setItem("selectedFontInitials", this.selectedFont);
      
      // IMPORTANT: Also update field preferences with font size for consistent display
      if (!this.field.preferences) {
        this.field.preferences = {}
      }
      this.field.preferences.font_size = this.fontSize
    },
    drawImage(event) {
      this.remove();
      this.isSignatureStarted = true;
      const file = event.target.files[0];
      if (file && file.type.match("image.*")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = async () => {
            const canvas = this.$refs.canvas;
            const context = canvas.getContext("2d");
            const aspectRatio = img.width / img.height;
            const canvasWidth = canvas.width / scale;
            const canvasHeight = canvas.height / scale;
            let targetWidth = canvasWidth;
            let targetHeight = canvasHeight;

            if (canvasWidth / canvasHeight > aspectRatio) {
              targetWidth = canvasHeight * aspectRatio;
            } else {
              targetHeight = canvasWidth / aspectRatio;
            }

            if (targetHeight > targetWidth) {
              const scale = targetHeight / targetWidth;
              targetWidth *= scale;
              targetHeight *= scale;
            }

            const x = (canvasWidth - targetWidth) / 2;
            const y = (canvasHeight - targetHeight) / 2;

            setTimeout(async () => {
              context.clearRect(0, 0, canvasWidth, canvasHeight);
              context.drawImage(img, x, y, targetWidth, targetHeight);

              const imageData = context.getImageData(
                0,
                0,
                canvas.width,
                canvas.height
              );
              const data = imageData.data;
              for (let i = 0; i < data.length; i += 4) {
                if (data[i] > 230 && data[i + 1] > 230 && data[i + 2] > 230) {
                  data[i + 3] = 0;
                }
              }

              context.putImageData(imageData, 0, 0);
              this.$emit("start");

              await this.submit();
            }, 50);
          };
        };
        reader.readAsDataURL(file);
        this.uploadImageInputKey = Math.random().toString();
      }
    },
    toggleBold() {
      this.isBold = !this.isBold;
      this.updateWrittenInitials();
    },
    toggleItalic() {
      this.isItalic = !this.isItalic;
      this.updateWrittenInitials();
    },
    toggleUnderline() {
      this.isUnderline = !this.isUnderline;
      this.updateWrittenInitials();
    },
    toggleTextInput() {
      this.isDrawInitials = !this.isDrawInitials;
      this.modeToggled = true;

      // Clear model value — show saved profile value for this mode if exists
      this.$emit('update:model-value', '');
      this.isUsePreviousValue = true;

      // Clear only the input field for the current mode being left
      if (this.isDrawInitials && this.$refs.textInput) {
        this.$refs.textInput.value = '';
      }
      if (!this.isDrawInitials && this.pad) {
        this.pad.clear();
      }

      if (!this.isDrawInitials) {
        this.$nextTick(() => {
          this.$refs.textInput?.focus();
          this.$emit("start");
          this.pad.off();
        });
      } else {
        this.pad.on();
      }
    },
    updateFontSize() {
      const newSize = parseInt(this.fontSize);
      if (!isNaN(newSize) && !this.fontSizes.includes(newSize)) {
        this.fontSizes.push(newSize);
        this.fontSizes.sort((a, b) => a - b);
      }
      this.updateWrittenInitials();
    },
    async submit() {
      if (this.modelValue || this.computedPreviousValue) {
        if (this.computedPreviousValue) {
          this.$emit("update:model-value", this.computedPreviousValue);
        }

        return Promise.resolve({});
      }

      return new Promise((resolve) => {
        cropCanvasAndExportToPNG(this.$refs.canvas).then(async (blob) => {
          const file = new File([blob], "initials.png", { type: "image/png" });

          if (this.dryRun) {
            const reader = new FileReader();

            reader.readAsDataURL(file);

            reader.onloadend = () => {
              const attachment = {
                url: reader.result,
                uuid: Math.random().toString(),
              };

              this.$emit("attached", attachment);
              this.$emit("update:model-value", attachment.uuid);

              resolve(attachment);
            };
          } else {
            const formData = new FormData();

            formData.append("file", file);
            formData.append("submitter_slug", this.submitterSlug);
            formData.append("name", "attachments");

            // Update initials on profile
            this.updateInitialsOnProfile(formData);

            return fetch(this.baseUrl + "/api/attachments", {
              method: "POST",
              body: formData,
            })
              .then((resp) => resp.json())
              .then((attachment) => {
                this.$emit("attached", attachment);
                this.$emit("update:model-value", attachment.uuid);

                // Notify parent to update reactive state so next fields use this value
                const format = this.isDrawInitials ? 'drawn' : 'typed';
                this.$emit('profile-updated', { format, uuid: attachment.uuid });

                return resolve(attachment);
              });
          }
        });
      });
    },
    async updateInitialsOnProfile(formData) {
      formData.append("initials_format", this.isDrawInitials ? "drawn" : "typed");

      await fetch(this.baseUrl + "/user_initials", {
        method: "PUT",
        body: formData,
      });
    },
  },
};
</script>

<style scoped>
@font-face {
  font-family: "Italianno";
  src: url("../fonts/Italianno-Regular.ttf");
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: "Cookie";
  src: url("../fonts/Cookie-Regular.ttf");
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: "Parisienne";
  src: url("../fonts/Parisienne-Regular.ttf");
  font-weight: normal;
  font-style: normal;
}
</style>
